import { useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { addFavorite, listFavoriteIds, listFavorites, removeFavorite, type FavoritesListResponse } from '@/api/favorites';
import type { FavoriteIdsResponse, FavoriteMutationResponse } from '@/api/contracts/favorites';
import { canUseFavorites } from '@/constants/role-policy';
import { buildLoginRedirectHref } from '@/lib/auth-redirect';
import { favoritesKeys, itemKeys } from '@/lib/query-keys';
import { useAuthStore } from '@/stores/auth-store';
import type { ItemDetail, ItemListItem, PaginatedResponse } from '@/types';

interface FavoritesQueryOptions {
  enabled?: boolean;
}

interface FavoriteMutationContext {
  previousIds?: FavoriteIdsResponse;
  previousList?: FavoritesListResponse;
  previousItemDetail?: ItemDetail;
  previousItemLists: Array<[QueryKey, InfiniteData<PaginatedResponse<ItemListItem>> | undefined]>;
}

type SavedCountResolver = (currentSavedCount: number) => number;

function updateFavoriteIds(
  current: FavoriteIdsResponse | undefined,
  itemId: number,
  favorited: boolean,
): FavoriteIdsResponse {
  const currentIds = current?.itemIds ?? [];
  const nextIds = favorited
    ? Array.from(new Set([itemId, ...currentIds]))
    : currentIds.filter((id) => id !== itemId);

  return {
    itemIds: nextIds,
    count: nextIds.length,
  };
}

function removeFromFavoritesList(
  current: FavoritesListResponse | undefined,
  itemId: number,
): FavoritesListResponse | undefined {
  if (!current) {
    return current;
  }

  const nextItems = current.items.filter((item) => item.id !== itemId);
  const nextIds = current.itemIds.filter((id) => id !== itemId);

  return {
    items: nextItems,
    itemIds: nextIds,
    count: nextItems.length,
  };
}

function clampSavedCount(value: number): number {
  return Math.max(0, value);
}

function updateSavedCountInItems(
  items: ItemListItem[],
  itemId: number,
  resolveNextSavedCount: SavedCountResolver,
): ItemListItem[] {
  let changed = false;

  const nextItems = items.map((item) => {
    if (item.id !== itemId) {
      return item;
    }

    const nextSavedCount = clampSavedCount(resolveNextSavedCount(item.savedCount));

    if (nextSavedCount === item.savedCount) {
      return item;
    }

    changed = true;

    return {
      ...item,
      savedCount: nextSavedCount,
    };
  });

  return changed ? nextItems : items;
}

function updateSavedCountInInfiniteItemList(
  current: InfiniteData<PaginatedResponse<ItemListItem>> | undefined,
  itemId: number,
  resolveNextSavedCount: SavedCountResolver,
): InfiniteData<PaginatedResponse<ItemListItem>> | undefined {
  if (!current) {
    return current;
  }

  let changed = false;

  const nextPages = current.pages.map((page) => {
    const nextItems = updateSavedCountInItems(page.data, itemId, resolveNextSavedCount);

    if (nextItems === page.data) {
      return page;
    }

    changed = true;

    return {
      ...page,
      data: nextItems,
    };
  });

  return changed
    ? {
      ...current,
      pages: nextPages,
    }
    : current;
}

function updateSavedCountInFavoritesList(
  current: FavoritesListResponse | undefined,
  itemId: number,
  resolveNextSavedCount: SavedCountResolver,
): FavoritesListResponse | undefined {
  if (!current) {
    return current;
  }

  const nextItems = updateSavedCountInItems(current.items, itemId, resolveNextSavedCount);

  if (nextItems === current.items) {
    return current;
  }

  return {
    ...current,
    items: nextItems,
  };
}

function updateSavedCountInItemDetail(
  current: ItemDetail | undefined,
  itemId: number,
  resolveNextSavedCount: SavedCountResolver,
): ItemDetail | undefined {
  if (!current || current.id !== itemId) {
    return current;
  }

  const nextSavedCount = clampSavedCount(resolveNextSavedCount(current.savedCount));

  if (nextSavedCount === current.savedCount) {
    return current;
  }

  return {
    ...current,
    savedCount: nextSavedCount,
  };
}

function isItemListQuery(queryKey: QueryKey): boolean {
  return queryKey[0] === itemKeys.all[0] && queryKey[1] === 'list';
}

function getItemListSnapshots(queryClient: QueryClient) {
  return queryClient.getQueriesData<InfiniteData<PaginatedResponse<ItemListItem>>>({
    predicate: (query) => isItemListQuery(query.queryKey),
  });
}

function applySavedCountUpdate(
  queryClient: QueryClient,
  itemId: number,
  resolveNextSavedCount: SavedCountResolver,
): void {
  getItemListSnapshots(queryClient).forEach(([queryKey]) => {
    queryClient.setQueryData<InfiniteData<PaginatedResponse<ItemListItem>>>(
      queryKey,
      (current) => updateSavedCountInInfiniteItemList(current, itemId, resolveNextSavedCount),
    );
  });

  const currentDetail = queryClient.getQueryData<ItemDetail>(itemKeys.detail(itemId));

  if (currentDetail) {
    queryClient.setQueryData<ItemDetail>(
      itemKeys.detail(itemId),
      updateSavedCountInItemDetail(currentDetail, itemId, resolveNextSavedCount),
    );
  }

  const currentFavoritesList = queryClient.getQueryData<FavoritesListResponse>(favoritesKeys.list);

  if (currentFavoritesList) {
    queryClient.setQueryData<FavoritesListResponse>(
      favoritesKeys.list,
      updateSavedCountInFavoritesList(currentFavoritesList, itemId, resolveNextSavedCount),
    );
  }
}

function applySavedCountDelta(
  queryClient: QueryClient,
  itemId: number,
  delta: number,
): void {
  applySavedCountUpdate(queryClient, itemId, (currentSavedCount) => currentSavedCount + delta);
}

function applyExactSavedCount(
  queryClient: QueryClient,
  itemId: number,
  savedCount: number,
): void {
  const nextSavedCount = clampSavedCount(savedCount);
  applySavedCountUpdate(queryClient, itemId, () => nextSavedCount);
}

function restoreFavoriteMutationContext(
  queryClient: QueryClient,
  itemId: number,
  context: FavoriteMutationContext | undefined,
): void {
  if (!context) {
    return;
  }

  if (context.previousIds) {
    queryClient.setQueryData(favoritesKeys.ids, context.previousIds);
  }

  if (context.previousList) {
    queryClient.setQueryData(favoritesKeys.list, context.previousList);
  }

  if (context.previousItemDetail) {
    queryClient.setQueryData(itemKeys.detail(itemId), context.previousItemDetail);
  }

  context.previousItemLists.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
}

function useFavoritesQueryEnabled(enabled: boolean = true): boolean {
  const user = useAuthStore((state) => state.user);
  return canUseFavorites(user?.roles) && enabled;
}

export function useFavorites(options: FavoritesQueryOptions = {}) {
  const queryEnabled = useFavoritesQueryEnabled(options.enabled);

  return useQuery({
    queryKey: favoritesKeys.list,
    queryFn: listFavorites,
    enabled: queryEnabled,
  });
}

export function useFavoriteIds(options: FavoritesQueryOptions = {}) {
  const queryEnabled = useFavoritesQueryEnabled(options.enabled);

  return useQuery({
    queryKey: favoritesKeys.ids,
    queryFn: listFavoriteIds,
    enabled: queryEnabled,
  });
}

export function useAddFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: number) => addFavorite(itemId),
    onMutate: async (itemId): Promise<FavoriteMutationContext> => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: favoritesKeys.all }),
        queryClient.cancelQueries({ queryKey: itemKeys.all }),
        queryClient.cancelQueries({ queryKey: itemKeys.detail(itemId) }),
      ]);

      const previousIds = queryClient.getQueryData<FavoriteIdsResponse>(favoritesKeys.ids);
      const previousList = queryClient.getQueryData<FavoritesListResponse>(favoritesKeys.list);
      const previousItemDetail = queryClient.getQueryData<ItemDetail>(itemKeys.detail(itemId));
      const previousItemLists = getItemListSnapshots(queryClient);

      queryClient.setQueryData<FavoriteIdsResponse>(
        favoritesKeys.ids,
        updateFavoriteIds(previousIds, itemId, true),
      );
      applySavedCountDelta(queryClient, itemId, 1);

      return {
        previousIds,
        previousList,
        previousItemDetail,
        previousItemLists,
      };
    },
    onSuccess: (response, itemId) => {
      applyExactSavedCount(queryClient, itemId, response.savedCount);
    },
    onError: (_error, itemId, context) => {
      restoreFavoriteMutationContext(queryClient, itemId, context);
    },
    onSettled: (_response, _error, itemId) => {
      void queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
      void queryClient.invalidateQueries({ queryKey: itemKeys.all });
      void queryClient.invalidateQueries({ queryKey: itemKeys.detail(itemId) });
    },
  });
}

export function useRemoveFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: number) => removeFavorite(itemId),
    onMutate: async (itemId): Promise<FavoriteMutationContext> => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: favoritesKeys.all }),
        queryClient.cancelQueries({ queryKey: itemKeys.all }),
        queryClient.cancelQueries({ queryKey: itemKeys.detail(itemId) }),
      ]);

      const previousIds = queryClient.getQueryData<FavoriteIdsResponse>(favoritesKeys.ids);
      const previousList = queryClient.getQueryData<FavoritesListResponse>(favoritesKeys.list);
      const previousItemDetail = queryClient.getQueryData<ItemDetail>(itemKeys.detail(itemId));
      const previousItemLists = getItemListSnapshots(queryClient);

      queryClient.setQueryData<FavoriteIdsResponse>(
        favoritesKeys.ids,
        updateFavoriteIds(previousIds, itemId, false),
      );
      queryClient.setQueryData<FavoritesListResponse>(
        favoritesKeys.list,
        removeFromFavoritesList(previousList, itemId),
      );
      applySavedCountDelta(queryClient, itemId, -1);

      return {
        previousIds,
        previousList,
        previousItemDetail,
        previousItemLists,
      };
    },
    onSuccess: (response, itemId) => {
      applyExactSavedCount(queryClient, itemId, response.savedCount);
    },
    onError: (_error, itemId, context) => {
      restoreFavoriteMutationContext(queryClient, itemId, context);
    },
    onSettled: (_response, _error, itemId) => {
      void queryClient.invalidateQueries({ queryKey: favoritesKeys.all });
      void queryClient.invalidateQueries({ queryKey: itemKeys.all });
      void queryClient.invalidateQueries({ queryKey: itemKeys.detail(itemId) });
    },
  });
}

export function useToggleFavorite(itemId: number) {
  const favoriteIdsQuery = useFavoriteIds();
  const addFavoriteMutation = useAddFavorite();
  const removeFavoriteMutation = useRemoveFavorite();
  const isFavorited = favoriteIdsQuery.data?.itemIds.includes(itemId) ?? false;
  const isLoading =
    addFavoriteMutation.isPending ||
    removeFavoriteMutation.isPending;

  const toggleFavorite = async (): Promise<FavoriteMutationResponse> => {
    if (isFavorited) {
      return removeFavoriteMutation.mutateAsync(itemId);
    }

    return addFavoriteMutation.mutateAsync(itemId);
  };

  return {
    isFavorited,
    isLoading,
    toggleFavorite,
  };
}

export function useFavoriteAction(itemId: number) {
  const user = useAuthStore((state) => state.user);
  const favorite = useToggleFavorite(itemId);
  const canToggleFavorite = canUseFavorites(user?.roles);

  const toggleFavoriteOrRedirect = async (): Promise<FavoriteMutationResponse | null> => {
    if (!canToggleFavorite) {
      if (typeof window !== 'undefined') {
        window.location.assign(buildLoginRedirectHref(window.location.href));
      }

      return null;
    }

    return favorite.toggleFavorite();
  };

  return {
    ...favorite,
    canToggleFavorite,
    toggleFavoriteOrRedirect,
  };
}
